import { Box, Checkbox, FormControlLabel, Typography, Fade, IconButton } from '@mui/material'
import { useSelector, useDispatch } from 'react-redux'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
	useGetBenefitCodesQuery,
	useGetBenefitCodesRulesQuery,
} from '../../../../../store/api/timesheet/timesheetBenefitCodesApi'
import dayjs from 'dayjs'
import {
	BonusCode,
	handleTimesheetApprovalDetails,
	handleDetails,
} from '../../../../../store/slices/timesheet/timesheetPageSlice'
// import { useGetTimesheetQuery } from '../../../../../utils/hooks/useGetTimesheetQuery'
import { useIsTimesheetApproved } from '../../../../../utils/hooks'
import { TimesheetStoreDispatch, TimesheetStoreState } from '../../../../../store/timesheetStore'
import Tooltip from '@mui/material/Tooltip'

export default function BonusCodes(): React.JSX.Element {
	// const { data: timesheetData } = useGetTimesheetQuery()
	const dispatch = useDispatch<TimesheetStoreDispatch>()
	// Redux selectors
	const timesheetPage = useSelector((state: TimesheetStoreState) => state.timesheetPage)
	const timesheetWorkTimes = useSelector((state: TimesheetStoreState) => state.timesheetApprovalDetailsAccept.workTimes)
	const bonus = useSelector((state: TimesheetStoreState) => state.timesheetPage.bonusCodes)

	const isTimeSheetApproved = useIsTimesheetApproved()

	/**
	 * Validates a dateTime string and returns it if valid, otherwise returns null.
	 * @param dateTime - The dateTime string to validate.
	 * @returns The validated dateTime string or null if invalid.
	 */
	const validateDateTime = useCallback((dateTime: string | null | undefined) => {
		if (!dateTime) return null
		return dayjs(dateTime).isValid() ? dateTime : null
	}, [])

	// Memoizes the driverId to avoid unnecessary recalculations
	const driverId = useMemo(() => {
		if (!timesheetPage.driver?.driverId) {
			console.error('Driver ID is not available in timesheetPage')
			return 0
		}
		return timesheetPage.driver.driverId
	}, [timesheetPage.driver?.driverId])

	// Fetches benefit codes and rules using custom hooks
	const { currentData: benefitCodesData } = useGetBenefitCodesQuery(driverId)

	const benefitCodesRulesParams = useMemo(
		() => ({
			driverId: timesheetPage.driver.driverId,
			begin1: validateDateTime(timesheetWorkTimes[0]?.startTime),
			end1: validateDateTime(timesheetWorkTimes[0]?.endTime),
			begin2: validateDateTime(timesheetWorkTimes[1]?.startTime),
			end2: validateDateTime(timesheetWorkTimes[1]?.endTime),
			begin3: validateDateTime(timesheetWorkTimes[2]?.startTime),
			end3: validateDateTime(timesheetWorkTimes[2]?.endTime),
		}),
		[timesheetPage.driver.driverId, timesheetWorkTimes, validateDateTime],
	)

	// Fetches rules for benefit codes
	const { data: disabledBonusCodes } = useGetBenefitCodesRulesQuery(benefitCodesRulesParams)

	// Memoizes rules to avoid unnecessary recomputation
	const rules = useMemo(
		() =>
			disabledBonusCodes?.data?.map((item: { codigo1: string; codigo2: string; temDireitoPorHorario: boolean }) => ({
				codigo1: item.codigo1.toString(),
				codigo2: item.codigo2.toString(),
				temDireitoPorHorario: item.temDireitoPorHorario,
			})) ?? [],
		[disabledBonusCodes],
	)

	// Local state variables to manage bonus codes and their selection
	const [allBonusCodes, setAllBonusCodes] = useState<boolean>(false)
	const [benefitCodes, setBenefitCodes] = useState<BonusCode[]>([])
	const [selectedCodes, setSelectedCodes] = useState<BonusCode[]>([])
	const [bonusCodesToShow, setBonusCodesToShow] = useState<BonusCode[]>([])

	// Update selected codes when Redux state changes
	useEffect(() => {
		if (isTimeSheetApproved) {
			setSelectedCodes(timesheetPage.approval?.timeSheetApprovalCodes as BonusCode[])
		} else {
			setSelectedCodes(timesheetPage.bonusCodes)
		}
	}, [bonus, timesheetPage.approval?.timeSheetApprovalCodes, timesheetPage.bonusCodes])

	// Update benefit codes when fetched from API
	useEffect(() => {
		setBenefitCodes(benefitCodesData ?? [])
	}, [benefitCodesData])

	// Update codes to show based on selection and display mode
	useEffect(() => {
		if (allBonusCodes && benefitCodes.length > 0) {
			// Show all unique codes
			const uniqueCodes = benefitCodes.filter((code, idx, self) => idx === self.findIndex(c => c.code === code.code))
			setBonusCodesToShow(uniqueCodes)
		} else if (selectedCodes?.length > 0) {
			setBonusCodesToShow(selectedCodes)
		} else {
			setBonusCodesToShow([])
		}
	}, [allBonusCodes, benefitCodes, selectedCodes])

	/**
	 * Checks if a bonus code is selected by its ID.
	 * @param codeId - The ID of the bonus code to check.
	 * @returns A boolean indicating whether the bonus code is selected.
	 */
	const isBonusCodeSelected = useCallback(
		(codeId: number): boolean => {
			return selectedCodes?.some(code => code.codeId === codeId)
		},
		[selectedCodes],
	)

	/**
	 * Generates an object mapping codes to the codes they disable.
	 * @param rules - An array of rule objects containing codigo1 and codigo2.
	 * @returns An object where each key is a codigo1 and its value is an array of codigo2.
	 */
	const generateRulesObject = useCallback(
		(
			rules: {
				codigo1: string
				codigo2: string
				temDireitoPorHorario: boolean
			}[],
		) => {
			const rulesObject: Record<string, string[]> = {}
			rules.forEach(rule => {
				if (!rulesObject[rule.codigo1]) {
					rulesObject[rule.codigo1] = []
				}
				rulesObject[rule.codigo1].push(rule.codigo2)
			})
			return rulesObject
		},
		[],
	)

	// Memoizes the rules object
	const rulesObject = useMemo(() => (rules.length > 0 ? generateRulesObject(rules) : {}), [rules, generateRulesObject])

	/**
	 * Checks if a bonus code has a conflict based on the selected codes and the rules.
	 * @param code - The code of the bonus to check.
	 * @returns A boolean indicating whether the bonus code has a conflict.
	 * This function iterates through the rules and checks if the selected code is in the disables array.
	 */
	const hasConflict = useCallback(
		(code: string): boolean => {
			if (!rules || rules.length === 0) return false
			const selectedCodesActive: string[] = selectedCodes?.map(c => c.code)
			// Generate generic disabling rules as an array of objects
			const disablingRules = Object.entries(rulesObject).map(([selected, disables]) => ({ selected, disables }))
			// Check if any rule applies
			for (const rule of disablingRules) {
				if (rule.selected === code) {
					if (rule.disables?.some(disabledCode => selectedCodesActive?.includes(disabledCode))) {
						return true
					}
				}
			}
			return false
		},
		[rules, selectedCodes, rulesObject],
	)

	/**
	 * Handles the selection and deselection of bonus codes.
	 * @param event - The change event from the checkbox input.
	 * This function updates the selected codes based on user interaction with the checkboxes.
	 * It checks if the code is already selected and either adds or removes it accordingly.
	 */
	const handleBenefits = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const codeId = parseInt(event.target.value, 10)
			const isSelected = selectedCodes?.some(code => code.codeId === codeId)
			let updatedSelectedCodes: BonusCode[] = []
			if (isSelected) {
				updatedSelectedCodes = selectedCodes.filter(code => code.codeId !== codeId)
			} else {
				const selectedCode = benefitCodes.find(code => code.codeId === codeId)
				if (selectedCode) {
					updatedSelectedCodes = selectedCodes?.length ? [...selectedCodes, selectedCode] : [selectedCode]
				} else {
					updatedSelectedCodes = selectedCodes?.length ? [...selectedCodes] : []
				}
			}
			// Sort for visual consistency
			updatedSelectedCodes = updatedSelectedCodes.sort((a, b) => a.codeId - b.codeId)

			setSelectedCodes(updatedSelectedCodes)

			// Update the global state
			dispatch(handleDetails({ bonusCodes: updatedSelectedCodes })) // Update bonus codes in global state
			dispatch(handleTimesheetApprovalDetails({ timeSheetApprovalCodes: updatedSelectedCodes })) // Update approval codes in global state
		},
		[selectedCodes, benefitCodes, dispatch, timesheetPage],
	)

	const renderedBonusCodes = useMemo(() => {
		if (bonusCodesToShow.length === 0) return null

		return bonusCodesToShow.map((code, index) => {
			const isSelected = isBonusCodeSelected(code.codeId)
			const conflicting = hasConflict(code.code)
			const shouldDisable = conflicting && !isSelected

			return (
				<Fade
					key={code.codeId}
					in={true}
					timeout={50 + index * 15}
					style={{ transitionDelay: `${index * 15}ms` }}
				>
					<Tooltip
						title={
							conflicting && !shouldDisable
								? `Este código entra em conflito com o código ${rulesObject[code.code].join(', ')}.`
								: ''
						}
					>
						<FormControlLabel
							sx={{
								width: 'calc((100% / 4) - 3rem)',
								display: 'flex',
								margin: '0px !important',
								alignItems: 'center',
								['& .MuiFormControlLabel-label']: {
									fontFamily: 'var(--font-family)',
									fontSize: '0.8125rem',
									fontWeight: 500,
									lineHeight: '130%',
									paddingTop: '0.2rem',
									color: shouldDisable ? 'text.disabled' : conflicting ? 'error.main' : 'var(--primary-dark)',
								},
								opacity: shouldDisable ? 0.6 : 1,
							}}
							labelPlacement='end'
							value={code.codeId}
							control={
								<Checkbox
									checked={isSelected}
									onChange={handleBenefits}
									disabled={shouldDisable}
									size='small'
									sx={{
										color: conflicting ? 'error.main' : undefined,
										'&.Mui-checked': {
											color: conflicting ? 'error.main' : undefined,
										},
									}}
								/>
							}
							disabled={shouldDisable}
							label={`${code.code} - ${code.description}`}
						/>
					</Tooltip>
				</Fade>
			)
		})
	}, [bonusCodesToShow, isBonusCodeSelected, hasConflict, handleBenefits, rulesObject])

	return (
		<Box
			sx={{
				display: 'grid',
				gap: 1,
				paddingBlockEnd: 1,
				borderBottom: '1px solid rgba(134, 149, 175, 0.15)',
			}}
		>
			<Typography
				variant='subtitle1'
				sx={{
					fontFamily: 'var(--font-family)',
					fontSize: '1rem',
					fontWeight: 600,
					lineHeight: '160%',
					color: 'var(--primary-dark)',
				}}
			>
				Abonos e Refeições Aplicáveis
			</Typography>
			<Box sx={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem' }}>
				<Box sx={{ flexGrow: 1 }}>
					<Box
						sx={{
							display: 'flex',
							flexWrap: 'wrap',
							margin: '0.5rem 0',
							columnGap: '1rem',
						}}
					>
						{bonusCodesToShow.length === 0 && (
							<Typography
								variant='body1'
								sx={{
									fontFamily: 'var(--font-family)',
									fontSize: '0.875rem',
									fontWeight: 600,
									lineHeight: '157%',
									color: 'var(--primary-light)',
								}}
							>
								Nenhuma bonificação selecionada.
							</Typography>
						)}
						{renderedBonusCodes}
					</Box>
				</Box>
				<Box sx={{ display: 'flex', justifyContent: 'center' }}>
					<IconButton
						tabIndex={-1}
						onClick={() => setAllBonusCodes(!allBonusCodes)}
						size='small'
						aria-label={allBonusCodes ? 'esconder bonificações' : 'mostrar todas as bonificações'}
					>
						{allBonusCodes ? (
							<KeyboardArrowUpIcon
								fontSize='small'
								sx={{ color: 'var(--primary)' }}
							/>
						) : (
							<KeyboardArrowDownIcon
								fontSize='small'
								sx={{ color: 'var(--primary)' }}
							/>
						)}
					</IconButton>
				</Box>
			</Box>
		</Box>
	)
}
